import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as solidIcons from '@fortawesome/free-solid-svg-icons';
import { useBusinessData } from '../../context/BusinessContext';
import '../../css/ServiceDetail.css';

// Icon mapping function to get the right FontAwesome icon from the string in JSON
const iconMapping = (iconName) => {
  return iconName && solidIcons[iconName] ? solidIcons[iconName] : solidIcons.faChartLine;
};

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const { loading, error, getData, getImageUrl } = useBusinessData();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return <div className="error">Error loading data: {error}</div>;
  }

  // Get all services
  const services = getData('services', []);
  
  // Find the current service by ID
  const service = services.find(s => s.id === serviceId);
  
  // If service not found
  if (!service) {
    return (
      <div className="service-not-found">
        <div className="container">
          <h2>{getData('ui.serviceDetail.notFoundSection.title', 'Service Not Found')}</h2>
          <p>{getData('ui.serviceDetail.notFoundSection.message', 'The service you\'re looking for does not exist.')}</p>
          <Link to="/services" className="btn btn-primary">
            <FontAwesomeIcon icon={solidIcons.faArrowLeft} /> {getData('ui.global.backButton', 'Back')} to Services
          </Link>
        </div>
      </div>
    );
  }
  
  // Get the service hero background image using getImageUrl
  const servicesHeroImage = getImageUrl('images.backgrounds.services', 'services-hero.jpg');
  
  // Get the specific service image - simplified handling for external URLs
  let serviceImage;
  
  // If service.image is a full URL, use it directly
  if (service.image && service.image.startsWith('http')) {
    serviceImage = service.image;
  } 
  // Try to get it from the service's ID mapping to our JSON structure
  else if (service.id) {
    // Simple mapping of service IDs to our JSON keys
    const serviceImageMap = {
      'business-analysis': 'businessAnalysis',
      'digital-transformation': 'digitalTransformation',
      'marketing-strategy': 'marketingStrategy',
      'partnership-development': 'partnershipDevelopment',
      'growth-strategy': 'growthStrategy',
      'executive-coaching': 'executiveCoaching'
    };
    
    const imageKey = serviceImageMap[service.id] || service.id;
    serviceImage = getImageUrl(`images.services.${imageKey}`, service.image || servicesHeroImage);
  }
  // Fallback to generic service hero image
  else {
    serviceImage = servicesHeroImage;
  }
  
  // Create inline style for background image - using URL directly
  const serviceDetailHeroStyle = {
    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('${serviceImage}')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  };

  return (
    <>
      {/* Service Hero */}
      <div className="service-detail-hero" style={serviceDetailHeroStyle}>
        <div className="container">
          <div className="service-detail-hero-content">
            <div className="service-detail-icon">
              <FontAwesomeIcon icon={iconMapping(service.icon)} />
            </div>
            <h1>{service.title}</h1>
            <p>{service.description}</p>
          </div>
        </div>
      </div>
      
      {/* Service Detail Content */}
      <section className="section">
        <div className="container">
          <div className="service-detail-content">
            <div className="service-detail-image">
              <img 
                src={serviceImage} 
                alt={service.title} 
              />
            </div>
            <div className="service-detail-text">
              <h2>{getData('ui.serviceDetail.mainSection.title', 'How We Can Help')}</h2>
              <p>{service.detailedDescription}</p>
              
              <h3>{getData('ui.serviceDetail.featuresSection.title', 'Key Features')}</h3>
              <ul className="service-detail-features">
                {service.features.map((feature, index) => (
                  <li key={index}>
                    <FontAwesomeIcon icon={solidIcons.faChartLine} className="feature-icon" />
                    {feature}
                  </li>
                ))}
              </ul>
              
              <div className="service-detail-cta">
                <Link to="/contact" className="btn btn-primary">Request a Consultation</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Related Services */}
      <section className="section section-dark">
        <div className="container">
          <h2 className="section-title">{getData('ui.serviceDetail.relatedSection.title', 'Related Services')}</h2>
          <p className="section-subtitle">{getData('ui.serviceDetail.relatedSection.subtitle', 'Explore other services that might interest you')}</p>
          
          <div className="related-services">
            {services
              .filter(s => s.id !== serviceId)
              .slice(0, 3)
              .map((relatedService, index) => (
                <div className="related-service-card" key={index}>
                  <div className="related-service-icon">
                    <FontAwesomeIcon icon={iconMapping(relatedService.icon)} />
                  </div>
                  <h3>{relatedService.title}</h3>
                  <p>{relatedService.description.substring(0, 100)}...</p>
                  {relatedService.id && (
                    <Link to={`/services/${relatedService.id}`} className="btn-service">
                      {getData('ui.global.learnMoreButton', 'Learn More')}
                    </Link>
                  )}
                </div>
              ))}
          </div>
          
          <div className="back-to-services">
            <Link to="/services" className="btn-back">
              <FontAwesomeIcon icon={solidIcons.faArrowLeft} /> {getData('ui.global.backButton', 'Back')} to All Services
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default ServiceDetail; 